// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import {SOMETHING_WENT_WRONG_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import type {KeybindCommand} from '@app/features/input/state/InputKeybind';
import Keybind from '@app/features/input/state/InputKeybind';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {openRoomOutfit} from '@app/features/voice_scene/commands/RoomOutfitCommands';
import {
	canSendEmote,
	ownsRoomEmote,
	sendEmote,
	sendPoint,
	sendSlap,
} from '@app/features/voice_scene/commands/RoomSignalCommands';
import {useCameraMode} from '@app/features/voice_scene/components/useCameraMode';
import styles from '@app/features/voice_scene/components/VoiceSceneActionDock.module.css';
import {useFocusBackOnClose} from '@app/features/voice_scene/hooks/useFocusBackOnClose';
import {STAND_UP_DESCRIPTOR} from '@app/features/voice_scene/hooks/useRoomEmotes';
import type {RoomBodyEmote, SceneCameraMode, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomShortcutGuide from '@app/features/voice_scene/state/RoomShortcutGuide';
import VoiceSceneCamera from '@app/features/voice_scene/state/VoiceSceneCamera';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	ArrowBendDownRightIcon,
	ArrowDownIcon,
	ArrowFatUpIcon,
	CaretDownIcon,
	CaretUpIcon,
	CrosshairIcon,
	EyeIcon,
	FootprintsIcon,
	HandPalmIcon,
	HandWavingIcon,
	type Icon,
	LockIcon,
	MusicNotesIcon,
	QuestionIcon,
	SquaresFourIcon,
	StarIcon,
	TShirtIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {type Participant, type Room, RoomEvent} from 'livekit-client';
import {observer} from 'mobx-react-lite';
import {type CSSProperties, type MouseEvent, type RefObject, useEffect, useReducer, useRef} from 'react';

const DOCK_DESCRIPTOR = msg({
	message: 'Room actions',
	comment: 'Voice call 3D room: accessible name of the bar of character actions (movements, outfit, eyes, shortcuts).',
});
export const WAVE_DESCRIPTOR = msg({
	message: 'Wave',
	comment: 'Voice call 3D room: button label. Your character waves at everyone in the room.',
});
const IN_SHOP_DESCRIPTOR = msg({
	message: 'In shop',
	comment:
		'Voice call 3D room: note on a character movement the user does not have yet, next to a lock; choosing it opens the movement in the shop.',
});
const MORE_DESCRIPTOR = msg({
	message: 'Movements',
	comment: 'Voice call 3D room: button that opens every character movement (dance, jump, faint, slap...).',
});
const OUTFIT_DESCRIPTOR = msg({
	message: 'Outfit',
	comment: 'Voice call 3D room: button that opens the room character outfit editor.',
});
const EYES_DESCRIPTOR = msg({
	message: 'Eyes',
	comment:
		"Voice call 3D room: toggle label. On, the camera looks out through your own character's eyes (first person); off, back to the whole room.",
});
const FOLLOW_DESCRIPTOR = msg({
	message: 'Follow',
	context: 'voice-scene-dock',
	comment: 'Voice call 3D room: toggle label. On, the camera follows behind your walking character.',
});
const SHORTCUTS_DESCRIPTOR = msg({
	message: 'Shortcuts',
	context: 'voice-scene-dock',
	comment: 'Voice call 3D room: button that opens the card with every keyboard shortcut of the room.',
});

const BODY_CHOICES: ReadonlyArray<{kind: RoomBodyEmote | 'wave'; key: string; icon: Icon; label: MessageDescriptor}> = [
	{kind: 'wave', key: '1', icon: HandWavingIcon, label: WAVE_DESCRIPTOR},
	{
		kind: 'dance',
		key: '2',
		icon: MusicNotesIcon,
		label: msg({message: 'Dance', comment: 'Voice call 3D room: character dance movement.'}),
	},
	{
		kind: 'jump',
		key: '3',
		icon: ArrowFatUpIcon,
		label: msg({
			message: 'Jump',
			context: 'voice-scene-emote',
			comment: 'Voice call 3D room: character jump movement.',
		}),
	},
	{
		kind: 'crouch',
		key: '4',
		icon: ArrowDownIcon,
		label: msg({message: 'Crouch', comment: 'Voice call 3D room: character crouching movement.'}),
	},
	{
		kind: 'punch',
		key: '5',
		icon: StarIcon,
		label: msg({message: 'Punch', comment: 'Voice call 3D room: playful character punch movement.'}),
	},
	{
		kind: 'faint',
		key: '6',
		icon: ArrowBendDownRightIcon,
		label: msg({message: 'Faint', comment: 'Voice call 3D room: character fainting movement.'}),
	},
];
// Everyone's, never in the shop; by their place on the keyboard (useRoomEmotes).
const AIMED_CHOICES = [
	{
		id: 'point',
		key: 'B',
		icon: CrosshairIcon,
		send: sendPoint,
		label: msg({
			message: 'Point',
			context: 'voice-scene-emote',
			comment:
				'Voice call 3D room: character movement. Your character points at what your mouse pointer is over (where your camera looks while the pointer is captured), for everyone.',
		}),
	},
	{
		id: 'slap',
		key: 'T',
		icon: HandPalmIcon,
		send: sendSlap,
		label: msg({
			message: 'Slap',
			comment:
				'Voice call 3D room: playful character movement. Your character slaps the person standing in front of it; it only lands on them if they allow slaps.',
		}),
	},
] as const;

export interface DockAction {
	id: string;
	icon: Icon;
	label: MessageDescriptor;
	// The key shown on the button; null when it has none (or the user bound it to a combination).
	key: string | null;
	group: 'movement' | 'me' | 'guide';
	// Cannot run now (seated, or my emote limit holds); still focusable, with `hint` as its tooltip.
	unavailable?: boolean;
	hint?: MessageDescriptor;
	// A movement I do not own: running it opens the shop.
	locked?: boolean;
	pressed?: boolean;
	// Opens a picker: aria-expanded instead of aria-pressed.
	expanded?: boolean;
	run: () => void;
}

// A movement I do not have opens its shop dialog; one the shop does not list opens the shop.
async function openEmoteInShop(kind: RoomBodyEmote, i18n: I18n): Promise<void> {
	try {
		await ShopCatalog.load();
		const entry = ShopCatalog.itemEntries.find(({item}) => item.type === 'room_emote' && item.model_key === kind);
		if (!entry) {
			RouterUtils.transitionTo(Routes.SHOP);
			return;
		}
		const {ShopItemDetailModal} = await import('@app/features/shop/components/modals/ShopItemDetailModal');
		ModalCommands.pushWithKey(
			ModalCommands.modal(() => (
				<ShopItemDetailModal entry={entry} data-flx="voice-scene.voice-scene-action-dock.shop-item-detail-modal" />
			)),
			`shop-item-${entry.item.id}`,
		);
	} catch {
		ToastCommands.error(i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR));
	}
}

// A single bare key the user's binding uses (V, F); a combination has no badge, its tooltip still names the action.
function boundKey(action: KeybindCommand): string | null {
	const {combo} = Keybind.getByAction(action);
	if (combo.ctrl || combo.ctrlOrMeta || combo.meta || combo.alt || combo.shift) return null;
	return combo.key?.length === 1 ? combo.key.toUpperCase() : null;
}

// Every action the dock can offer right now, in the dock's order. Observable reads only: an observer calling it
// redraws when standing, the limit, ownership or the camera changes, never per frame.
export function roomDockActions({
	standing,
	mode,
	i18n,
}: {
	standing: boolean;
	mode: SceneCameraMode;
	i18n: I18n;
}): Array<DockAction> {
	const limited = !canSendEmote();
	const movements: Array<DockAction> = BODY_CHOICES.map(({kind, key, icon, label}) => {
		if (kind !== 'wave' && !ownsRoomEmote(kind))
			return {
				id: kind,
				icon,
				label,
				key: null,
				group: 'movement',
				locked: true,
				hint: IN_SHOP_DESCRIPTOR,
				run: () => void openEmoteInShop(kind, i18n),
			};
		const seated = kind !== 'wave' && !standing;
		return {
			id: kind,
			icon,
			label,
			key,
			group: 'movement',
			unavailable: limited || seated,
			hint: seated ? STAND_UP_DESCRIPTOR : undefined,
			run: () => {
				if (!seated) sendEmote({kind});
			},
		};
	});
	for (const {id, key, icon, label, send} of AIMED_CHOICES) {
		// The registered room is observable: point and slap appear once its characters (and their clips) are in.
		if (!VoiceSceneCamera.room?.handle.canEmote?.(id)) continue;
		movements.push({
			id,
			icon,
			label,
			key,
			group: 'movement',
			unavailable: limited || !standing,
			hint: standing ? undefined : STAND_UP_DESCRIPTOR,
			run: () => {
				if (standing) send();
			},
		});
	}
	const me: Array<DockAction> = [
		{id: 'outfit', icon: TShirtIcon, label: OUTFIT_DESCRIPTOR, key: 'O', group: 'me', run: openRoomOutfit},
	];
	// The seat view keeps its own camera: no eyes, no follow there (as the toolbar had it).
	if (VoiceSceneCamera.available && mode !== 'seat')
		me.push({
			id: 'eyes',
			icon: EyeIcon,
			label: EYES_DESCRIPTOR,
			key: boundKey('misc_toggle_room_eyes'),
			group: 'me',
			pressed: mode === 'eyes',
			run: () => VoiceSceneCamera.toggleEyes(),
		});
	if (VoiceSceneCamera.canFollow && mode !== 'seat')
		me.push({
			id: 'follow',
			icon: FootprintsIcon,
			label: FOLLOW_DESCRIPTOR,
			key: boundKey('misc_toggle_room_follow'),
			group: 'me',
			pressed: mode === 'follow',
			run: () => VoiceSceneCamera.toggleFollow(),
		});
	return [...movements, ...me];
}

const byId = (actions: ReadonlyArray<DockAction>, ids: ReadonlyArray<string>): Array<DockAction> =>
	ids.flatMap((id) => actions.filter((action) => action.id === id));

const KeyBadge = observer(function KeyBadge({children, className}: {children: string | null; className?: string}) {
	if (!children || Accessibility.hideKeyboardHints) return null;
	return (
		<kbd className={clsx(styles.key, className)} aria-hidden data-flx="voice-scene.voice-scene-action-dock.key">
			{children}
		</kbd>
	);
});

const ACTION_WITH_KEY_DESCRIPTOR = msg({
	message: '{action} ({key})',
	comment:
		'Voice call 3D room: tooltip of an action button with its keyboard key. Preserve {action} and {key}; {action} is the action name, already translated, and {key} a key such as 2 or T.',
});
const ACTION_WITH_HINT_DESCRIPTOR = msg({
	message: '{action}: {hint}',
	comment:
		'Voice call 3D room: tooltip of an action button that cannot run now, with why. Preserve {action} and {hint}; both are already translated (e.g. "Dance: Stand up first (E)").',
});

function tooltipOf(i18n: I18n, action: DockAction): string {
	const label = i18n._(action.label);
	if (action.hint) return i18n._(ACTION_WITH_HINT_DESCRIPTOR, {action: label, hint: i18n._(action.hint)});
	return action.key ? i18n._(ACTION_WITH_KEY_DESCRIPTOR, {action: label, key: action.key}) : label;
}

// Mouse presses keep the focus on the room canvas, so the walking and movement keys keep working after a click.
const keepFocus = (event: {preventDefault: () => void}) => event.preventDefault();

function ActionButton({action, variant, onRan}: {action: DockAction; variant: 'tile' | 'orb'; onRan?: () => void}) {
	const {i18n} = useLingui();
	const label = i18n._(action.label);
	return (
		<Tooltip
			text={tooltipOf(i18n, action)}
			position="top"
			data-flx="voice-scene.voice-scene-action-dock.action-button.tooltip"
		>
			<FocusRing offset={-2} data-flx="voice-scene.voice-scene-action-dock.action-button.focus-ring">
				<button
					type="button"
					className={clsx(
						styles[variant],
						(action.pressed || action.expanded) && styles.pressed,
						action.locked && styles.locked,
					)}
					aria-disabled={action.unavailable || undefined}
					aria-pressed={action.pressed}
					aria-expanded={action.expanded}
					aria-keyshortcuts={action.key ?? undefined}
					aria-label={variant === 'orb' ? label : undefined}
					data-action={action.id}
					onMouseDown={keepFocus}
					onClick={() => {
						if (action.unavailable) return;
						action.run();
						onRan?.();
					}}
					data-flx="voice-scene.voice-scene-action-dock.action-button.button"
				>
					<span className={styles.iconWrap} data-flx="voice-scene.voice-scene-action-dock.action-button.icon-wrap">
						<action.icon
							weight={action.pressed ? 'fill' : 'bold'}
							className={styles.icon}
							data-flx="voice-scene.voice-scene-action-dock.action-button.icon"
						/>
						{action.locked ? (
							<LockIcon
								weight="fill"
								className={styles.lock}
								data-flx="voice-scene.voice-scene-action-dock.action-button.lock"
							/>
						) : null}
					</span>
					<span className={styles.label} data-flx="voice-scene.voice-scene-action-dock.action-button.label">
						{label}
					</span>
					<KeyBadge data-flx="voice-scene.voice-scene-action-dock.action-button.key-badge">{action.key}</KeyBadge>
				</button>
			</FocusRing>
		</Tooltip>
	);
}

function guideActions(): {more: DockAction; help: DockAction} {
	return {
		more: {
			id: 'more',
			icon: SquaresFourIcon,
			label: MORE_DESCRIPTOR,
			key: 'G',
			group: 'guide',
			expanded: RoomShortcutGuide.moreOpen,
			run: RoomShortcutGuide.toggleMore,
		},
		help: {
			id: 'help',
			icon: QuestionIcon,
			label: SHORTCUTS_DESCRIPTOR,
			key: '?',
			group: 'guide',
			pressed: RoomShortcutGuide.cardOpen,
			run: RoomShortcutGuide.toggleCard,
		},
	};
}

// While open, a press outside `ref` closes the picker (one listener for the whole picker).
function useCloseOnOutsidePress(open: boolean, ref: RefObject<HTMLElement | null>): void {
	useEffect(() => {
		if (!open) return;
		const doc = ref.current?.ownerDocument ?? document;
		const onPress = (event: PointerEvent) => {
			if (!ref.current?.contains(event.target as Node)) RoomShortcutGuide.closeAll();
		};
		doc.addEventListener('pointerdown', onPress, true);
		return () => doc.removeEventListener('pointerdown', onPress, true);
	}, [open, ref]);
}

const HIDE_DOCK_DESCRIPTOR = msg({
	message: 'Hide actions',
	comment:
		'Voice call 3D room: small button at the end of the action bar; it tucks the bar away to a small tab. The keys keep working.',
});
const SHOW_DOCK_DESCRIPTOR = msg({
	message: 'Show actions',
	comment: 'Voice call 3D room: accessible name of the small tab at the bottom that brings the hidden action bar back.',
});
const ACTIONS_TAB_DESCRIPTOR = msg({
	message: 'Actions',
	context: 'voice-scene-dock-tab',
	comment: 'Voice call 3D room: short word on the small tab that brings the hidden action bar back.',
});

// Every movement on a ring over the tile (or the tab) that opens it, its name and key on each slot.
function MovementWheel({movements}: {movements: ReadonlyArray<DockAction>}) {
	const {i18n} = useLingui();
	return (
		<div
			className={styles.wheel}
			role="group"
			aria-label={i18n._(MORE_DESCRIPTOR)}
			style={{'--wheel-count': movements.length} as CSSProperties}
			data-flx="voice-scene.voice-scene-action-dock.wheel"
		>
			<span className={styles.wheelHub} aria-hidden data-flx="voice-scene.voice-scene-action-dock.wheel-hub">
				{i18n._(MORE_DESCRIPTOR)}
				<KeyBadge className={styles.hubKey}>G</KeyBadge>
			</span>
			{movements.map((action, index) => (
				<span
					key={action.id}
					className={styles.wheelSlot}
					style={{'--wheel-index': index} as CSSProperties}
					data-flx="voice-scene.voice-scene-action-dock.wheel-slot"
				>
					<ActionButton action={action} variant="orb" onRan={RoomShortcutGuide.closeAll} />
				</span>
			))}
		</div>
	);
}

// A game-style hotbar at the bottom; the four everyday movements and my eyes are one click, the rest sits in a wheel
// over the Movements tile (G). Its last button tucks it away to a small tab; the keys, G's wheel included, work on.
const DockBar = observer(function DockBar({
	actions,
	canvas,
}: {
	actions: ReadonlyArray<DockAction>;
	canvas: HTMLCanvasElement | null;
}) {
	const {i18n} = useLingui();
	const ref = useRef<HTMLDivElement>(null);
	const toggleRef = useRef<HTMLButtonElement>(null);
	const focusToggle = useRef(false);
	const {more, help} = guideActions();
	const open = RoomShortcutGuide.moreOpen;
	const hidden = RoomShortcutGuide.dockHidden;
	useCloseOnOutsidePress(open, ref);
	useFocusBackOnClose(open, ref, canvas);
	// Hide and show swap places: a keyboard press carries the focus over to the one that takes its place.
	useEffect(() => {
		if (!focusToggle.current) return;
		focusToggle.current = false;
		toggleRef.current?.focus({preventScroll: true});
	}, [hidden]);
	const toggle = (event: MouseEvent<HTMLButtonElement>) => {
		focusToggle.current = event.currentTarget.ownerDocument.activeElement === event.currentTarget;
		RoomShortcutGuide.toggleDockHidden();
	};
	const wheel = open ? <MovementWheel movements={actions.filter((action) => action.group === 'movement')} /> : null;
	if (hidden)
		return (
			<div className={styles.bottomLane} data-action-dock="bottom" data-flx="voice-scene.voice-scene-action-dock.lane">
				<div ref={ref} className={styles.wheelAnchor} data-flx="voice-scene.voice-scene-action-dock.wheel-anchor">
					<FocusRing offset={-2} data-flx="voice-scene.voice-scene-action-dock.show.focus-ring">
						<button
							ref={toggleRef}
							type="button"
							className={styles.showTab}
							aria-label={i18n._(SHOW_DOCK_DESCRIPTOR)}
							onMouseDown={keepFocus}
							onClick={toggle}
							data-flx="voice-scene.voice-scene-action-dock.show"
						>
							<CaretUpIcon
								weight="bold"
								className={styles.caret}
								data-flx="voice-scene.voice-scene-action-dock.show-icon"
							/>
							{i18n._(ACTIONS_TAB_DESCRIPTOR)}
						</button>
					</FocusRing>
					{wheel}
				</div>
			</div>
		);
	return (
		<div className={styles.bottomLane} data-action-dock="bottom" data-flx="voice-scene.voice-scene-action-dock.lane">
			<div
				ref={ref}
				className={styles.bar}
				role="toolbar"
				aria-label={i18n._(DOCK_DESCRIPTOR)}
				data-flx="voice-scene.voice-scene-action-dock.bar"
			>
				{byId(actions, ['wave', 'dance', 'slap', 'point']).map((action) => (
					<ActionButton key={action.id} action={action} variant="tile" />
				))}
				<div className={styles.wheelAnchor} data-flx="voice-scene.voice-scene-action-dock.wheel-anchor">
					<ActionButton action={more} variant="tile" />
					{wheel}
				</div>
				<span className={styles.divider} aria-hidden data-flx="voice-scene.voice-scene-action-dock.divider" />
				{byId(actions, ['outfit', 'eyes', 'follow']).map((action) => (
					<ActionButton key={action.id} action={action} variant="tile" />
				))}
				<ActionButton action={help} variant="tile" />
				<Tooltip text={i18n._(HIDE_DOCK_DESCRIPTOR)} data-flx="voice-scene.voice-scene-action-dock.hide.tooltip">
					<FocusRing offset={-2} data-flx="voice-scene.voice-scene-action-dock.hide.focus-ring">
						<button
							ref={toggleRef}
							type="button"
							className={styles.hide}
							aria-label={i18n._(HIDE_DOCK_DESCRIPTOR)}
							onMouseDown={keepFocus}
							onClick={toggle}
							data-flx="voice-scene.voice-scene-action-dock.hide"
						>
							<CaretDownIcon
								weight="bold"
								className={styles.caret}
								data-flx="voice-scene.voice-scene-action-dock.hide-icon"
							/>
						</button>
					</FocusRing>
				</Tooltip>
			</div>
		</div>
	);
});

// My own micup.emotes attribute (what I own) lives on the LiveKit participant, not in MobX: a movement bought during
// the call unlocks when the server writes it.
function useOwnEmotesRedraw(room: Room | null): void {
	const [, redraw] = useReducer((count: number) => count + 1, 0);
	useEffect(() => {
		if (!room) return;
		const onAttributes = (_changed: Record<string, string>, participant: Participant) => {
			if (participant === room.localParticipant) redraw();
		};
		room.on(RoomEvent.ParticipantAttributesChanged, onAttributes);
		return () => {
			room.off(RoomEvent.ParticipantAttributesChanged, onAttributes);
		};
	}, [room]);
}

// My character's clips load after the room registers it, and the renderer does not say when: while point and slap are
// missing the dock looks again a few times (a character whose clip file has neither stops there).
const CLIP_RECHECK_MS = [1000, 3000, 8000, 15000];
function useClipRecheck(missing: boolean): void {
	const [, recheck] = useReducer((count: number) => count + 1, 0);
	useEffect(() => {
		if (!missing) return;
		const timers = CLIP_RECHECK_MS.map((ms) => window.setTimeout(recheck, ms));
		return () => {
			for (const timer of timers) window.clearTimeout(timer);
		};
	}, [missing]);
}

// The room's character actions, each with its key on it. Only with characters on screen.
export const VoiceSceneActionDock = observer(function VoiceSceneActionDock({
	handle,
	standing,
	canvas,
}: {
	handle: VoiceSceneHandle | null;
	standing: boolean;
	canvas: HTMLCanvasElement | null;
}) {
	const {i18n} = useLingui();
	const mode = useCameraMode(handle);
	const assignment = readVoiceRoomAssignment(ExperimentAssignments.response);
	const visible = handle !== null && assignment.avatars && !VoiceSceneSettings.classicView;
	useOwnEmotesRedraw(MediaEngine.room);
	const actions = visible ? roomDockActions({standing, mode, i18n}) : [];
	useClipRecheck(visible && VoiceSceneCamera.room !== null && !actions.some(({id}) => id === 'point' || id === 'slap'));
	if (!visible) return null;
	return <DockBar actions={actions} canvas={canvas} />;
});
